import React from 'react';
import { Link } from 'react-router-dom';
import { Workflow, Zap, Shield, BarChart3, ArrowRight, Layers, Bot } from 'lucide-react';

const features = [
  {
    icon: Workflow,
    title: 'Multi-Step AI Workflows',
    description: 'Break complex tasks into intelligent workflow graphs. Each step uses the right AI model for the job.',
  },
  {
    icon: Layers,
    title: 'Template Library',
    description: 'Start from proven templates or build your own. Save successful workflows and reuse them instantly.',
  },
  {
    icon: Bot,
    title: 'Smart Model Selection',
    description: 'Automatically selects the best AI model for each task based on complexity, cost, and capabilities.',
  },
  {
    icon: Zap,
    title: 'Tools & Connectors',
    description: 'Connect to external services, process documents, generate reports, and automate end-to-end.',
  },
  {
    icon: BarChart3,
    title: 'Cost Controls',
    description: 'Real-time usage tracking, per-workflow cost estimates, and billing controls. No surprise bills.',
  },
  {
    icon: Shield,
    title: 'Secure by Design',
    description: 'Your data stays yours. No training on your content. GDPR compliant. Encrypted in transit and at rest.',
  },
];

const useCases = [
  { title: 'Marketing', description: 'Generate ad copy, analyse competitors, create social campaigns — all in one workflow.' },
  { title: 'Finance', description: 'Extract data from statements, categorise transactions, generate expense reports automatically.' },
  { title: 'Operations', description: 'Process documents, draft proposals, summarise meetings, and track action items.' },
];

export function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero with nav overlay */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(/hero-network.svg)', backgroundSize: 'cover', backgroundColor: '#05091C' }}
        />
        {/* Nav — transparent, overlaying hero */}
        <nav className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Workflow size={28} className="text-blue-400" />
            <span className="text-xl font-bold text-white">Flowize</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/pricing" className="text-sm text-gray-300 hover:text-white">Pricing</Link>
            <Link to="/login" className="text-sm text-gray-300 hover:text-white">Log in</Link>
            <a href="mailto:hello@flowize.xyz" className="text-sm bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-400 transition-colors">
              Get in touch
            </a>
          </div>
        </nav>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-32 text-center">
          <h1 className="text-5xl font-bold text-white mb-6 leading-tight">
            Turn complex tasks into<br />
            <span className="text-blue-400">intelligent AI workflows</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-10">
            Describe what you need. Flowize breaks it into steps, selects the right AI models,
            and executes the entire pipeline — so you get results, not prompts.
          </p>
          <div className="flex items-center justify-center gap-4">
            <a
              href="mailto:hello@flowize.xyz"
              className="bg-blue-500 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-blue-400 transition-colors flex items-center gap-2"
            >
              Get in touch <ArrowRight size={20} />
            </a>
            <Link
              to="/pricing"
              className="border border-gray-500 text-gray-200 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-white/10 transition-colors"
            >
              View Pricing
            </Link>
          </div>
          <p className="mt-4 text-sm text-gray-400">Get started from £10</p>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">How it works</h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            From task description to completed workflow in three steps.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Describe your task', desc: 'Tell Flowize what you need in plain language. Upload documents, provide context, set constraints.' },
              { step: '2', title: 'AI builds the workflow', desc: 'The platform analyses your task, breaks it into steps, selects models, and builds an execution graph.' },
              { step: '3', title: 'Review & get results', desc: 'Watch the workflow execute in real-time. Download reports, CSV files, or formatted documents.' },
            ].map((item) => (
              <div key={item.step} className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                <div className="w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-semibold text-lg mb-4 tabular-nums">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Built for serious work</h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Not another chatbot wrapper. Flowize orchestrates multi-step AI pipelines with the right model for each task.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div key={feature.title} className="p-6">
                  <Icon size={24} className="text-indigo-600 mb-3" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Works across industries</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {useCases.map((uc) => (
              <div key={uc.title} className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{uc.title}</h3>
                <p className="text-gray-600">{uc.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to automate your workflows?</h2>
          <p className="text-gray-600 mb-8">Start with Pay As You Go — from £10. Scale up as you grow.</p>
          <a
            href="mailto:hello@flowize.xyz"
            className="inline-flex items-center gap-2 bg-indigo-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-indigo-700 active:bg-indigo-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
          >
            Get in touch <ArrowRight size={20} />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Workflow size={20} className="text-gray-400" />
              <span className="text-sm text-gray-500">&copy; 2026 Flowize. All rights reserved.</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/pricing" className="text-sm text-gray-500 hover:text-gray-700">Pricing</Link>
              <Link to="/help" className="text-sm text-gray-500 hover:text-gray-700">Help</Link>
              <Link to="/terms" className="text-sm text-gray-500 hover:text-gray-700">Terms</Link>
              <Link to="/privacy" className="text-sm text-gray-500 hover:text-gray-700">Privacy</Link>
              <a href="mailto:hello@flowize.xyz" className="text-sm text-gray-500 hover:text-gray-700">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
